import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdArrowBack, 
  MdRocketLaunch,
  MdPeople,
  MdCheckCircle,
  MdCancel,
  MdInfoOutline,
  MdAdd,
  MdVisibility,
  MdEmail,
  MdPhone,
  MdSchool,
  MdSearch,
  MdRefresh
} from 'react-icons/md';

/**
 * Tutor Portal Access Setup Component
 * View portal setups and create new ones
 */
function TutorPortalAccessSetup() {
  const navigate = useNavigate();
  const { getToken, user } = useAttendanceAuth();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // View mode: 'list' or 'form' or 'details'
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    tutor_id: '',
    auto_generate_password: true,
    send_credentials_email: false,
    show_only_without_users: true,
    show_only_with_subjects: true
  });

  // Options data
  const [allTutors, setAllTutors] = useState([]); // For form dropdown
  const [portalTutors, setPortalTutors] = useState([]); // For list view (deprecated - using setupRecords)
  const [setupRecords, setSetupRecords] = useState([]); // Portal User Setup Records (wizard records)
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedTutorDetails, setSelectedTutorDetails] = useState(null);
  const [stats, setStats] = useState({
    portal_users_created: 0,
    users_skipped: 0
  });

  useEffect(() => {
    if (viewMode === 'list') {
      loadPortalTutors();
      loadStats();
    }
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === 'form' && formData.show_only_without_users !== undefined) {
      loadTutorsForForm();
    }
  }, [viewMode, formData.show_only_without_users, formData.show_only_with_subjects]);

  useEffect(() => {
    if (viewMode === 'form' && formData.tutor_id) {
      loadTutorDetails();
    } else if (viewMode === 'form') {
      setSelectedTutor(null);
    }
  }, [formData.tutor_id, viewMode]);

  useEffect(() => {
    applySearchFilter();
  }, [searchTerm, setupRecords]);

  const loadPortalTutors = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      
      console.log('=== Loading Portal Setup Records ===');
      
      // First, try to load wizard records (Portal User Setup Records)
      try {
        console.log('Attempting to load wizard records...');
        const currentUserId = user?.user_id || user?.id;
        
        console.log('=== User ID Check ===');
        console.log('User object:', user);
        console.log('Current user_id:', currentUserId);
        console.log('User.user_id:', user?.user_id);
        console.log('User.id:', user?.id);
        
        // Pass user_id to backend if available (backend should filter server-side)
        const params = { limit: 1000 };
        if (currentUserId) {
          params.user_id = currentUserId;
          params.created_by = currentUserId; // Try both field names
          params.create_uid = currentUserId; // Odoo standard field name
          params.include_create_uid = true; // Request backend to include create_uid in response
          console.log('✅ Passing user_id to backend for filtering:', currentUserId);
          console.log('Params being sent:', JSON.stringify(params, null, 2));
        } else {
          console.error('❌ No user_id found! Cannot filter records.');
          console.error('User object keys:', user ? Object.keys(user) : 'user is null');
        }
        
        const wizardResult = await attendanceAPI.getPortalSetupRecords(params, token);
        console.log('Wizard records response:', wizardResult);
        console.log('Number of records returned:', wizardResult.records?.length || wizardResult.wizard_records?.length || 0);
        
        if (wizardResult.success) {
          // Check different possible response structures
          let records = wizardResult.records || wizardResult.wizard_records || wizardResult.data || [];
          
          if (Array.isArray(records) && records.length > 0) {
            console.log(`✅ Loaded ${records.length} wizard records (before filtering):`, records);
            
            // Check if backend already filtered (if we passed user_id and got records, assume backend filtered)
            const backendFiltered = currentUserId && (params.user_id || params.created_by || params.create_uid);
            
            // Filter records to only show those created by the current user
            // Check for create_uid, created_by, user_id, or create_user_id fields
            const currentUserIdForFilter = user?.user_id || user?.id;
            const currentEmployeeId = user?.employee_id;
            
            console.log('=== User Filtering Debug ===');
            console.log('Current user object:', user);
            console.log('Current user_id:', currentUserIdForFilter);
            console.log('Current employee_id:', currentEmployeeId);
            console.log('Total records before filtering:', records.length);
            console.log('Backend filtered (user_id passed):', backendFiltered);
            console.log('Params passed to backend:', params);
            
            // Log first record structure to see what fields are available
            if (records.length > 0) {
              console.log('Sample record structure:', JSON.stringify(records[0], null, 2));
              console.log('Sample record keys:', Object.keys(records[0]));
              
              // Check for any field that might contain creator info
              const firstRecord = records[0];
              console.log('Checking for creator fields in first record:');
              Object.keys(firstRecord).forEach(key => {
                if (key.toLowerCase().includes('create') || 
                    key.toLowerCase().includes('user') || 
                    key.toLowerCase().includes('uid') ||
                    key.toLowerCase().includes('author')) {
                  console.log(`  ${key}:`, firstRecord[key]);
                }
              });
            }
            
            // If we passed user_id to backend, backend SHOULD filter server-side
            // But if backend isn't filtering, we need to check if create_uid exists for frontend filtering
            const hasCreateUidInRecords = records.length > 0 && (
              records[0].create_uid !== undefined ||
              records[0].created_by !== undefined ||
              records[0].create_user_id !== undefined
            );
            
            console.log('Has create_uid field in records for frontend filtering:', hasCreateUidInRecords);
            
            if (backendFiltered && !hasCreateUidInRecords) {
              // We passed user_id, backend should filter, but no create_uid in response
              // Trust backend filtered (even though we can't verify)
              console.log('✅ Backend should have filtered records by user_id parameter');
              console.log('✅ Trusting backend filter - showing all returned records');
              console.log('⚠️ Note: Cannot verify filtering since create_uid not in response');
              // Backend filtered, so all records shown are for this user
              // No frontend filtering needed
            } else if (hasCreateUidInRecords && currentUserIdForFilter) {
              // Frontend filtering possible - create_uid field exists
              console.log('✅ create_uid field found - filtering on frontend');
              console.log('Frontend filtering: create_uid field found, filtering by current user');
              // Frontend filtering needed - backend didn't receive user_id or didn't filter
              console.log('⚠️ Frontend filtering: user_id was NOT passed to backend or backend did not filter');
              console.log('⚠️ Attempting to filter by create_uid field...');
              const filteredRecords = records.filter(record => {
                // Check multiple possible creator fields
                // In Odoo, create_uid is usually a Many2one field which can be:
                // - A number (id)
                // - A tuple [id, name]
                // - An object {id: x, name: y}
                const recordCreatorId = 
                  record.create_uid || 
                  record.create_uid?.[0] || // Odoo tuple format [id, name]
                  record.create_uid?.id || // Odoo object format {id: x, name: y}
                  record.created_by || 
                  record.created_by?.[0] ||
                  record.created_by?.id ||
                  record.user_id || 
                  record.create_user_id ||
                  record.creator_id ||
                  record.create_uid_id || // Sometimes it's create_uid_id
                  record.write_uid || // Fallback to write_uid if create_uid not available
                  record.write_uid?.[0] ||
                  record.write_uid?.id;
                
                // Also check if it's a tuple/array format
                const creatorId = Array.isArray(recordCreatorId) 
                  ? recordCreatorId[0] 
                  : (typeof recordCreatorId === 'object' && recordCreatorId !== null)
                    ? recordCreatorId.id
                    : recordCreatorId;
                
                // Compare as numbers or strings
                const matches = 
                  creatorId != null &&
                  (creatorId == currentUserIdForFilter || // Use == for type coercion
                   creatorId === currentUserIdForFilter ||
                   String(creatorId) === String(currentUserIdForFilter));
                
                console.log(`Record ${record.id} (${record.employee_name || record.tutor_name || 'N/A'}):`, {
                  recordCreatorId: recordCreatorId,
                  creatorId: creatorId,
                  currentUserId: currentUserIdForFilter,
                  matches: matches,
                  create_uid: record.create_uid,
                  created_by: record.created_by,
                  user_id: record.user_id,
                  write_uid: record.write_uid,
                  allKeys: Object.keys(record)
                });
                
                return matches;
              });
              
              records = filteredRecords;
              console.log(`✅ Filtered to ${records.length} records created by current user (ID: ${currentUserIdForFilter})`);
              
              // If all records were filtered out, check if create_uid field exists in original records
              if (records.length === 0) {
                const originalRecords = wizardResult.records || wizardResult.wizard_records || wizardResult.data || [];
                const originalCount = originalRecords.length;
                if (originalCount > 0) {
                  // Check if any original record has create_uid field
                  const hasCreateUid = originalRecords.some(r => 
                    r.create_uid !== undefined || 
                    r.created_by !== undefined ||
                    r.create_user_id !== undefined
                  );
                  
                  if (!hasCreateUid) {
                    console.warn('⚠️ No create_uid field found in records. Backend should include this field for filtering.');
                    console.warn('⚠️ Since we passed user_id to backend, assuming backend filtered correctly.');
                    console.warn('⚠️ If backend is NOT filtering, it needs to be fixed to filter by user_id parameter.');
                    // If we passed user_id, trust backend filtered (even if it didn't)
                    // Otherwise show all records
                    if (backendFiltered) {
                      console.log('✅ Trusting backend filter (user_id was passed)');
                      records = originalRecords; // Show what backend returned
                    } else {
                      console.warn('⚠️ user_id was NOT passed - showing all records');
                      records = originalRecords;
                    }
                  } else {
                    console.warn('⚠️ All records were filtered out. Records were created by different users.');
                    console.warn('⚠️ This is correct behavior - you can only see records you created.');
                  }
                }
              }
            } else {
              console.warn('⚠️ No current user ID found!');
              console.warn('User object:', user);
              console.warn('Showing all records (no filtering applied)');
            }
            
            // Transform records to ensure they have the right structure
            const formattedRecords = records.map(record => ({
              id: record.id || record.wizard_id,
              record_name: record.record_name || `Portal Setup${record.tutor_name ? ` - ${record.tutor_name}` : ''}`,
              tutor_name: record.tutor_name || record.employee_name || record.name || 'N/A',
              tutor_id: record.tutor_id || record.employee_id,
              username: record.username || record.created_username || record.login || '',
              password: record.password || record.created_password || '************',
              created_date: record.created_date || record.create_date || null,
              employee_id: record.employee_id || record.tutor_id
            }));
            
            setSetupRecords(formattedRecords);
            setFilteredTutors(formattedRecords);
            return;
          } else {
            console.log('Wizard records endpoint returned empty array');
          }
        } else {
          console.log('Wizard records endpoint returned success: false');
        }
      } catch (wizardErr) {
        console.log('⚠️ Wizard records endpoint not available or failed:', wizardErr);
        console.log('Falling back to tutors list...');
        // Fall through to load from tutors list
      }
      
      // Fallback: Load all tutors with portal access and construct records
      console.log('Loading tutors list as fallback...');
      
      // Try to get tutors WITH portal access first (if the API supports it)
      // Then fall back to all tutors
      let result;
      try {
        // Try getting tutors that have portal access
        result = await attendanceAPI.getTutorsList({
          show_only_without_users: false,  // Don't filter out those with users
          show_only_with_subjects: false,   // Don't filter by subjects
          limit: 10000  // Get more tutors
        }, token);
      } catch (err) {
        console.error('Error loading tutors:', err);
        throw err;
      }

      console.log('Tutors list response:', result);

      if (result.success) {
        const allTutors = result.tutors || [];
        console.log(`Total tutors received: ${allTutors.length}`);
        
        // Log first few tutors to see their structure
        if (allTutors.length > 0) {
          console.log('=== Sample Tutor Structure ===');
          console.log('First tutor:', JSON.stringify(allTutors[0], null, 2));
          console.log('First tutor portal_access:', allTutors[0]?.portal_access);
          console.log('First tutor keys:', Object.keys(allTutors[0] || {}));
          
          // Check a few tutors to see portal access structure
          const sampleTutors = allTutors.slice(0, 5);
          sampleTutors.forEach((tutor, idx) => {
            console.log(`Tutor ${idx + 1} (${tutor.name}):`, {
              id: tutor.id,
              name: tutor.name,
              portal_access: tutor.portal_access,
              user_id: tutor.user_id,
              has_portal_access: tutor.portal_access?.has_portal_access,
              has_tutor_portal: tutor.portal_access?.has_tutor_portal,
              login: tutor.portal_access?.login,
              user_id_in_portal: tutor.portal_access?.user_id
            });
          });
        }
        
        // Filter to show only tutors with portal access
        // Be very permissive - check all possible ways
        const tutorsWithPortal = allTutors.filter(tutor => {
          const portalAccess = tutor.portal_access;
          
          // Check multiple conditions
          const hasAccess = (
            // Direct flags
            portalAccess?.has_portal_access === true ||
            portalAccess?.has_tutor_portal === true ||
            // Has user_id (any non-null value)
            (portalAccess?.user_id !== null && portalAccess?.user_id !== undefined && portalAccess?.user_id !== false && portalAccess?.user_id !== 0) ||
            // Has login (any non-empty string)
            (portalAccess?.login && portalAccess?.login !== '' && portalAccess?.login !== null) ||
            // Check tutor.user_id directly (some APIs might put it here)
            (tutor.user_id !== null && tutor.user_id !== undefined && tutor.user_id !== false && tutor.user_id !== 0)
          );
          
          if (hasAccess) {
            console.log(`✅ Tutor with portal access: ${tutor.name} (ID: ${tutor.id})`, {
              login: portalAccess?.login,
              has_portal_access: portalAccess?.has_portal_access,
              has_tutor_portal: portalAccess?.has_tutor_portal,
              user_id: portalAccess?.user_id,
              tutor_user_id: tutor.user_id
            });
          }
          
          return hasAccess;
        });
        
        console.log(`Tutors with portal access: ${tutorsWithPortal.length}`);
        
        // If no tutors found with portal access, check portal status individually
        // This is more reliable but slower - limit to first 50 tutors to avoid too many API calls
        if (tutorsWithPortal.length === 0) {
          console.warn('⚠️ No tutors matched portal access filter');
          console.log('Checking portal status individually for tutors...');
          
          const tutorsToCheck = allTutors.slice(0, 50); // Limit to first 50 to avoid too many API calls
          const tutorsWithPortalStatus = [];
          
          console.log(`Checking portal status for ${tutorsToCheck.length} tutors...`);
          
          // Check portal status in batches
          const batchSize = 5;
          for (let i = 0; i < tutorsToCheck.length; i += batchSize) {
            const batch = tutorsToCheck.slice(i, i + batchSize);
            const statusPromises = batch.map(async (tutor) => {
              try {
                const statusResult = await attendanceAPI.getTutorPortalStatus(tutor.id, token);
                if (statusResult.success && statusResult.portal_access?.has_portal_access) {
                  return { tutor, portalStatus: statusResult.portal_access };
                }
                return null;
              } catch (err) {
                console.error(`Error checking status for tutor ${tutor.id}:`, err);
                return null;
              }
            });
            
            const results = await Promise.all(statusPromises);
            results.forEach(result => {
              if (result) {
                tutorsWithPortalStatus.push({
                  ...result.tutor,
                  portal_access: result.portalStatus
                });
                console.log(`✅ Found tutor with portal access via status check: ${result.tutor.name}`, {
                  login: result.portalStatus.login,
                  has_portal_access: result.portalStatus.has_portal_access
                });
              }
            });
          }
          
          if (tutorsWithPortalStatus.length > 0) {
            console.log(`Found ${tutorsWithPortalStatus.length} tutors with portal access via status check`);
            const records = tutorsWithPortalStatus.map(tutor => ({
              id: tutor.id,
              record_name: `Portal Setup${tutor.name ? ` - ${tutor.name}` : ''}`,
              tutor_name: tutor.name,
              tutor_id: tutor.id,
              username: tutor.portal_access?.login || tutor.work_email || '',
              password: '************',
              created_date: null,
              employee_id: tutor.id
            }));
            
            setSetupRecords(records);
            setFilteredTutors(records);
            setPortalTutors(tutorsWithPortalStatus);
            return;
          }
          
          // Last resort: check for tutors with user_id directly
          console.log('Checking if any tutors have user_id directly...');
          const tutorsWithUser = allTutors.filter(tutor => {
            return tutor.user_id !== null && tutor.user_id !== undefined;
          });
          
          console.log(`Tutors with user_id: ${tutorsWithUser.length}`);
          
          if (tutorsWithUser.length > 0) {
            console.log('Found tutors with user_id, using those as portal users');
            const records = tutorsWithUser.map(tutor => ({
              id: tutor.id,
              record_name: `Portal Setup${tutor.name ? ` - ${tutor.name}` : ''}`,
              tutor_name: tutor.name,
              tutor_id: tutor.id,
              username: tutor.work_email || tutor.private_email || `user_${tutor.user_id}@portal.school.edu`,
              password: '************',
              created_date: null,
              employee_id: tutor.id
            }));
            
            setSetupRecords(records);
            setFilteredTutors(records);
            return;
          }
        }
        
        // Transform tutors into setup records format
        const records = tutorsWithPortal.map(tutor => ({
          id: tutor.id,
          record_name: `Portal Setup${tutor.name ? ` - ${tutor.name}` : ''}`,
          tutor_name: tutor.name,
          tutor_id: tutor.id,
          username: tutor.portal_access?.login || tutor.work_email || '',
          password: '************', // Always mask password (stored passwords not available from tutors endpoint)
          created_date: null,
          employee_id: tutor.id
        }));
        
        console.log('✅ Created setup records:', records);
        setSetupRecords(records);
        setFilteredTutors(records);
        setPortalTutors(tutorsWithPortal); // Keep for backward compatibility
        
        if (records.length === 0) {
          console.warn('⚠️ No tutors with portal access found');
          setError('No portal setups found. The tutors you created may not be in the current list, or the portal access information may not be updated yet. Try creating a new portal setup or check the browser console for details.');
        }
      } else {
        console.error('❌ Tutors list API failed:', result);
        setError(result.message || 'Failed to load portal setups');
      }
    } catch (err) {
      console.error('❌ Error loading portal tutors:', err);
      let errorMessage = err.message || 'Failed to load portal setups';
      
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        errorMessage = (
          'The Tutor Portal API endpoint is not available. ' +
          'Please ensure the backend API is properly configured and the routes are registered. ' +
          'Check the browser console (F12) for more details.'
        );
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadTutorsForForm = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const result = await attendanceAPI.getTutorsList({
        show_only_without_users: formData.show_only_without_users,
        show_only_with_subjects: formData.show_only_with_subjects,
        limit: 1000
      }, token);

      if (result.success) {
        setAllTutors(result.tutors || []);
      } else {
        setError(result.message || 'Failed to load tutors');
      }
    } catch (err) {
      console.error('Error loading tutors:', err);
      setError(err.message || 'Failed to load tutors');
    } finally {
      setLoading(false);
    }
  };

  const loadTutorDetails = async () => {
    try {
      const token = getToken();
      const result = await attendanceAPI.getTutorDetails(formData.tutor_id, token);
      if (result.success) {
        setSelectedTutor(result.tutor);
      }
    } catch (err) {
      console.error('Error loading tutor details:', err);
    }
  };

  const loadTutorDetailsForView = async (tutorId) => {
    try {
      setLoading(true);
      const token = getToken();
      const result = await attendanceAPI.getTutorDetails(tutorId, token);
      if (result.success) {
        setSelectedTutorDetails(result.tutor);
        setViewMode('details');
      }
    } catch (err) {
      console.error('Error loading tutor details:', err);
      setError('Failed to load tutor details');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = getToken();
      const result = await attendanceAPI.getTutorPortalStats(token);
      if (result.success && result.statistics) {
        // Stats are tracked per session
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const applySearchFilter = () => {
    if (!searchTerm) {
      setFilteredTutors(setupRecords);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = setupRecords.filter(record => 
      record.record_name?.toLowerCase().includes(term) ||
      record.tutor_name?.toLowerCase().includes(term) ||
      record.username?.toLowerCase().includes(term)
    );
    setFilteredTutors(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setSuccess(false);
    setError(null);
  };

  const handleCreatePortalUser = async (e) => {
    e.preventDefault();

    if (!formData.tutor_id) {
      setError('Please select a tutor');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      setSuccess(false);
      const token = getToken();

      const result = await attendanceAPI.createTutorPortalUser(
        parseInt(formData.tutor_id),
        {
          auto_generate_password: formData.auto_generate_password,
          send_credentials_email: formData.send_credentials_email
        },
        token
      );

      if (result.success) {
        setSuccess(true);
        setSuccessMessage(result.message || 'Portal user created successfully');
        
        // Update stats
        if (result.status === 'created') {
          setStats(prev => ({
            ...prev,
            portal_users_created: prev.portal_users_created + 1
          }));
        } else if (result.status === 'already_exists') {
          setStats(prev => ({
            ...prev,
            users_skipped: prev.users_skipped + 1
          }));
        }

        // Show password if available
        if (result.password) {
          setSuccessMessage(
            `${result.message}\n\nUsername: ${result.user?.login || 'N/A'}\nPassword: ${result.password}`
          );
        }

        // Reset form and go back to list, then reload
        setTimeout(() => {
          setViewMode('list');
          setFormData({
            tutor_id: '',
            auto_generate_password: true,
            send_credentials_email: false,
            show_only_without_users: true,
            show_only_with_subjects: true
          });
          setSuccess(false);
          // Reload the list after a short delay to ensure backend has updated
          setTimeout(() => {
            loadPortalTutors();
          }, 500);
        }, 2000);
      } else {
        setError(result.message || 'Failed to create portal user');
      }
    } catch (err) {
      console.error('Error creating portal user:', err);
      setError(err.message || 'Failed to create portal user');
    } finally {
      setCreating(false);
    }
  };

  const handleAddNew = () => {
    setViewMode('form');
    setError(null);
    setSuccess(false);
    setFormData({
      tutor_id: '',
      auto_generate_password: true,
      send_credentials_email: false,
      show_only_without_users: true,
      show_only_with_subjects: true
    });
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedTutorDetails(null);
    setError(null);
    setSuccess(false);
  };

  // List View
  if (viewMode === 'list') {
    return (
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/attendance/faculty-admin/dashboard')}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              title="Back to Dashboard"
            >
              <MdArrowBack className="text-2xl" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Tutor Portal Access Setup</h1>
              <p className="text-gray-600 mt-1">View and manage portal setups</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadPortalTutors}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition flex items-center space-x-2"
              disabled={loading}
            >
              <MdRefresh className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleAddNew}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition flex items-center space-x-2"
            >
              <MdAdd />
              <span>Add New</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <MdCancel className="text-red-500 text-xl" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by name, email, department, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing <strong>{filteredTutors.length}</strong> of <strong>{setupRecords.length}</strong> portal setups
        </div>

        {/* Portal Setup Records List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading && setupRecords.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading portal setups...</p>
            </div>
          ) : filteredTutors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MdPeople className="text-5xl mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No portal setups found</p>
              <p className="text-sm mt-2">
                {searchTerm
                  ? 'Try adjusting your search'
                  : 'Click "Add New" to create your first portal setup'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input type="checkbox" className="rounded border-gray-300" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Record Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tutor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Password
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTutors.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input type="checkbox" className="rounded border-gray-300" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{record.record_name || 'Portal Setup'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{record.tutor_name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{record.username || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{record.password || '************'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => record.tutor_id && loadTutorDetailsForView(record.tutor_id)}
                          className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                        >
                          <MdVisibility />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Details View
  if (viewMode === 'details' && selectedTutorDetails) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackToList}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              title="Back to List"
            >
              <MdArrowBack className="text-2xl" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Tutor Portal Details</h1>
              <p className="text-gray-600 mt-1">{selectedTutorDetails.name}</p>
            </div>
          </div>
        </div>

        {/* Details Content */}
        <div className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="text-base font-medium text-gray-900">{selectedTutorDetails.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Job Title</p>
                <p className="text-base font-medium text-gray-900">{selectedTutorDetails.job_title || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Department</p>
                <p className="text-base font-medium text-gray-900">{selectedTutorDetails.department_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedTutorDetails.active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {selectedTutorDetails.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 flex items-center space-x-1">
                  <MdEmail />
                  <span>Work Email</span>
                </p>
                <p className="text-base font-medium text-gray-900">{selectedTutorDetails.work_email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 flex items-center space-x-1">
                  <MdEmail />
                  <span>Private Email</span>
                </p>
                <p className="text-base font-medium text-gray-900">{selectedTutorDetails.private_email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 flex items-center space-x-1">
                  <MdPhone />
                  <span>Phone</span>
                </p>
                <p className="text-base font-medium text-gray-900">{selectedTutorDetails.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Portal Access */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Portal Access</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-sm text-gray-600">Username</p>
                <p className="text-base font-medium text-gray-900">
                  {selectedTutorDetails.portal_access?.login || 'N/A'}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div>
                  <p className="text-sm text-gray-600">Portal Access</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedTutorDetails.portal_access?.has_portal_access
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedTutorDetails.portal_access?.has_portal_access ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tutor Portal</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedTutorDetails.portal_access?.has_tutor_portal
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedTutorDetails.portal_access?.has_tutor_portal ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">User ID</p>
                <p className="text-base font-medium text-gray-900">
                  {selectedTutorDetails.portal_access?.user_id || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Subject Assignments */}
          {selectedTutorDetails.assignments && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Subject Assignments</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Assignments</p>
                <p className="text-2xl font-bold text-gray-900">
                  {selectedTutorDetails.assignments.total_count || selectedTutorDetails.subject_assignments_count || 0}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Active: {selectedTutorDetails.assignments.active_count || 0}
                </p>
              </div>
            </div>
          )}

          {/* Back Button */}
          <div className="flex justify-end">
            <button
              onClick={handleBackToList}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form View
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
            title="Back to List"
          >
            <MdArrowBack className="text-2xl" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Create Portal Setup</h1>
            <p className="text-gray-600 mt-1">Assign a tutor to the portal</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <MdCancel className="text-red-500 text-xl" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <MdCheckCircle className="text-green-500 text-xl mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-semibold mb-1">Success!</p>
              <pre className="text-green-700 text-sm whitespace-pre-wrap">{successMessage}</pre>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleCreatePortalUser} className="space-y-6">
        {/* Record Name */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-2">
            <label className="block text-sm font-semibold text-gray-700">
              Record Name?
            </label>
            <MdInfoOutline className="text-gray-400 text-sm" title="Record name for this setup" />
          </div>
          <input
            type="text"
            value="Portal Setup"
            readOnly
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
          />
        </div>

        {/* Tutor Selection */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-2">
            <label htmlFor="tutor_id" className="block text-sm font-semibold text-gray-700">
              Tutor?
            </label>
            <MdInfoOutline className="text-gray-400 text-sm" title="Select a tutor from employees" />
          </div>
          <select
            id="tutor_id"
            name="tutor_id"
            value={formData.tutor_id}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            disabled={loading}
          >
            <option value="">-- Select a tutor --</option>
            {allTutors.map(tutor => (
              <option key={tutor.id} value={tutor.id}>
                {tutor.name} {tutor.work_email ? `(${tutor.work_email})` : ''}
              </option>
            ))}
          </select>
          {loading && (
            <p className="mt-2 text-sm text-gray-500">Loading tutors...</p>
          )}
          {!loading && allTutors.length === 0 && (
            <p className="mt-2 text-sm text-gray-500">No tutors found matching the current filters.</p>
          )}
          {selectedTutor && (
            <div className="mt-3 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-gray-700">
                <strong>Department:</strong> {selectedTutor.department_name || 'N/A'}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Email:</strong> {selectedTutor.work_email || selectedTutor.private_email || 'N/A'}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Subject Assignments:</strong> {selectedTutor.subject_assignments_count || 0}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Portal Access:</strong> {selectedTutor.portal_access?.has_portal_access ? 'Yes' : 'No'}
              </p>
            </div>
          )}
        </div>

        {/* Auto-Generate Passwords */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="auto_generate_password"
              checked={formData.auto_generate_password}
              onChange={handleInputChange}
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm font-semibold text-gray-700">Auto-Generate Passwords?</span>
            <MdInfoOutline className="text-gray-400 text-sm" title="Automatically generate secure passwords for portal users" />
          </label>
        </div>

        {/* Send Credentials via Email */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="send_credentials_email"
              checked={formData.send_credentials_email}
              onChange={handleInputChange}
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm font-semibold text-gray-700">Send Credentials via Email?</span>
            <MdInfoOutline className="text-gray-400 text-sm" title="Send username and password via email to the tutor" />
          </label>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300"></div>

        {/* Configuration Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">CONFIGURATION FILTERS</h2>
          
          <div className="space-y-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="show_only_without_users"
                checked={formData.show_only_without_users}
                onChange={handleInputChange}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm font-semibold text-gray-700">Show Only Tutors Without Portal Access?</span>
              <MdInfoOutline className="text-gray-400 text-sm" title="Filter to show only tutors who don't have portal access yet" />
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="show_only_with_subjects"
                checked={formData.show_only_with_subjects}
                onChange={handleInputChange}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm font-semibold text-gray-700">Show Only Tutors With Subject Assignments?</span>
              <MdInfoOutline className="text-gray-400 text-sm" title="Filter to show only tutors who have subject assignments" />
            </label>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300"></div>

        {/* Results */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">RESULTS</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-md">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-semibold text-gray-700">Portal Users Created?</span>
                <MdInfoOutline className="text-gray-400 text-sm" title="Number of portal users created" />
              </div>
              <p className="text-2xl font-bold text-gray-800">{stats.portal_users_created}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-md">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-semibold text-gray-700">Users Skipped?</span>
                <MdInfoOutline className="text-gray-400 text-sm" title="Number of users skipped (already have access)" />
              </div>
              <p className="text-2xl font-bold text-gray-800">{stats.users_skipped}</p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={creating || !formData.tutor_id || loading}
            className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 rounded-lg flex items-center space-x-2 transition-colors shadow-lg"
          >
            <MdRocketLaunch className="text-xl" />
            <span>CREATE PORTAL USER FOR THIS TUTOR</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default TutorPortalAccessSetup;
